import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface VirtualListProps<T> {
  items: T[];
  /** Every row's height in pixels — rows are laid out at fixed intervals. */
  rowHeight: number;
  /** The scrolling window's height in pixels. */
  height: number;
  renderRow: (item: T, index: number) => ReactNode;
  getKey: (item: T, index: number) => string;
  /** Rows drawn beyond the window on each side, so a fast flick shows no gap. */
  overscan?: number;
  /** Shown instead of the list when there are no items. */
  empty?: ReactNode;
  className?: string;
  ariaLabel?: string;
}

/**
 * A long list that draws only the rows in view: 400 guests at the door, on a
 * phone, scroll as smoothly as forty. Rows must share one height; each is
 * placed absolutely at `index × rowHeight` inside a spacer as tall as all of
 * them, so the scrollbar still tells the truth.
 */
export function VirtualList<T>({
  items,
  rowHeight,
  height,
  renderRow,
  getKey,
  overscan = 6,
  empty,
  className = '',
  ariaLabel,
}: VirtualListProps<T>) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);

  // A list that got shorter (a filter) must not leave the window scrolled
  // past its end, showing nothing.
  const total = items.length * rowHeight;
  useEffect(() => {
    const el = ref.current;
    if (el && scrollTop > Math.max(0, total - height)) {
      const top = Math.max(0, total - height);
      el.scrollTop = top;
      setScrollTop(top);
    }
  }, [total, height, scrollTop]);

  if (items.length === 0) return <>{empty ?? null}</>;

  const first = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const last = Math.min(items.length, Math.ceil((scrollTop + height) / rowHeight) + overscan);
  const visible = items.slice(first, last);

  return (
    <div
      ref={ref}
      role="list"
      aria-label={ariaLabel}
      className={`relative overflow-y-auto ${className}`}
      style={{ height: Math.min(height, total) }}
      onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
    >
      <div style={{ height: total, position: 'relative' }}>
        {visible.map((item, i) => {
          const index = first + i;
          return (
            <div
              key={getKey(item, index)}
              role="listitem"
              style={{
                position: 'absolute',
                top: index * rowHeight,
                left: 0,
                right: 0,
                height: rowHeight,
              }}
            >
              {renderRow(item, index)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
